import { useState, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { Icon } from '../icon/icon'
import { getIconName } from '../icon/icon.helper'
import { Button } from '../button/button'
import Text from '../text/text'
import {
	Sheet,
	SheetContent,
	SheetDescription,
	SheetHeader,
	SheetTitle,
	SheetTrigger,
} from '../ui/sheet'
import type { EventCategory } from '~/types/eventCategory'
import type { CategoryNode } from './header-search-categories'

interface CategorySelectionSheetProps {
	categories: string[]
	setCategories: (categories: string[]) => void
	allCategories: EventCategory[]
	isOpen: boolean
	onOpenChange: (open: boolean) => void
	trigger: React.ReactNode
}

export default function CategorySelectionSheet({
	categories,
	setCategories,
	allCategories,
	isOpen,
	onOpenChange,
	trigger,
}: CategorySelectionSheetProps) {
	const { t } = useTranslation()
	const [searchQuery, setSearchQuery] = useState('')
	const [expandedGroups, setExpandedGroups] = useState<Set<string>>(new Set())

	// Process categories into hierarchical structure
	const processCategories = (
		cats: EventCategory[],
		parent?: CategoryNode,
		level = 0,
	): CategoryNode[] => {
		return cats.map(category => {
			const categoryNode: CategoryNode = {
				id: category.id,
				name: category.name,
				alias: category.alias,
				icon: category.icon,
				color: category.color,
				parent,
				level,
				children: [],
			}
			categoryNode.children = processCategories(
				category.children || [],
				categoryNode,
				level + 1,
			)
			return categoryNode
		})
	}

	const processedCategories = processCategories(allCategories)

	// Get all category IDs recursively
	const getAllCategoryIds = (category: CategoryNode): string[] => {
		const ids = [category.alias]
		category.children.forEach(child => {
			ids.push(...getAllCategoryIds(child))
		})
		return ids
	}

	// Get category by alias
	const getCategoryByAlias = (
		cats: CategoryNode[],
		alias: string,
	): CategoryNode | null => {
		for (const cat of cats) {
			if (cat.alias === alias) return cat
			const found = getCategoryByAlias(cat.children, alias)
			if (found) return found
		}
		return null
	}

	// Get all selected categories with their hierarchy info
	const getSelectedCategoriesWithInfo = () => {
		return categories
			.map(alias => {
				const category = getCategoryByAlias(processedCategories, alias)
				return category ? { ...category, alias } : null
			})
			.filter(Boolean) as (CategoryNode & { alias: string })[]
	}

	// Handle category toggle
	const handleCategoryToggle = (category: CategoryNode) => {
		const isSelected = categories.includes(category.alias)
		let newCategories: string[]

		if (isSelected) {
			// Remove category and all its children
			const idsToRemove = getAllCategoryIds(category)
			newCategories = categories.filter(id => !idsToRemove.includes(id))
		} else {
			// Add category
			newCategories = [...categories.filter(id => id !== 'all'), category.alias]
		}

		setCategories(newCategories)
	}

	// Handle "All Categories" toggle
	const handleAllToggle = () => {
		setCategories(['all'])
	}

	// Handle clear all
	const handleClearAll = () => {
		setCategories([])
		onOpenChange(false)
	}

	// Filter categories based on search
	const filterCategories = (cats: CategoryNode[]): CategoryNode[] => {
		if (!searchQuery) return cats

		return cats
			.map(category => {
				const matchesSearch = category.name
					.toLowerCase()
					.includes(searchQuery.toLowerCase())
				const filteredChildren = filterCategories(category.children)

				if (matchesSearch || filteredChildren.length > 0) {
					return { ...category, children: filteredChildren }
				}
				return null
			})
			.filter(Boolean) as CategoryNode[]
	}

	// Toggle group expansion
	const toggleGroup = (categoryId: string) => {
		setExpandedGroups(prev => {
			const newSet = new Set(prev)
			if (newSet.has(categoryId)) {
				newSet.delete(categoryId)
			} else {
				newSet.add(categoryId)
			}
			return newSet
		})
	}

	// Clear search when sheet closes
	useEffect(() => {
		if (!isOpen) {
			setSearchQuery('')
		}
	}, [isOpen])

	const filteredCategories = filterCategories(processedCategories)
	const selectedCategories = getSelectedCategoriesWithInfo()
	const isAllSelected = categories.length === 0 || categories.includes('all')

	// Render category option
	const renderCategoryOption = (category: CategoryNode): React.ReactNode => {
		const isSelected = categories.includes(category.alias)
		const hasChildren = category.children.length > 0
		const isExpanded = expandedGroups.has(category.id)
		const isParent = category.level === 0 && hasChildren

		return (
			<div key={category.id} className="w-full">
				<div
					className={`group flex items-center justify-between rounded-lg transition-all duration-200 ${
						isSelected
							? 'from-primary/15 to-secondary/10 border-primary/50 border-l-3 bg-gradient-to-r'
							: 'hover:bg-foreground/5 hover:shadow-sm'
					}`}
					style={{ paddingLeft: `${0.75 + category.level * 1.5}rem` }}
				>
					<button
						type="button"
						className="flex flex-1 items-center gap-3 p-3 text-left"
						onClick={() => handleCategoryToggle(category)}
					>
						<div
							className={`flex h-5 w-5 items-center justify-center rounded-md border-2 transition-all duration-200 ${
								isSelected
									? 'border-primary bg-primary text-primary-foreground scale-110'
									: 'border-border bg-background group-hover:border-primary/50'
							}`}
						>
							{isSelected && (
								<Icon
									name="check"
									size="xs"
									className="animate-in fade-in zoom-in duration-200"
								/>
							)}
						</div>
						<div
							className={`flex h-8 w-8 items-center justify-center rounded-full transition-all duration-200 bg-${category.color}-700 ${
								isSelected ? 'scale-110' : 'group-hover:scale-105'
							}`}
						>
							<Icon
								name={getIconName(category.icon)}
								size="xs"
								className="text-white"
							/>
						</div>
						<div className="flex flex-col">
							<span
								className={`font-medium transition-colors duration-200 ${
									isParent ? 'text-foreground' : 'text-foreground/90'
								} ${isSelected ? 'text-primary' : ''}`}
							>
								{category.name}
							</span>
							{isParent && (
								<span className="text-foreground/60 text-xs">
									{category.children.length}{' '}
									{category.children.length === 1
										? t('misc_subcategory')
										: t('misc_subcategories')}
								</span>
							)}
						</div>
					</button>
					{hasChildren && (
						<button
							type="button"
							onClick={() => toggleGroup(category.id)}
							className="hover:bg-foreground/10 ml-2 flex h-8 w-8 items-center justify-center rounded-full transition-all duration-200 hover:scale-105"
						>
							<Icon
								name="chevron-down"
								size="xs"
								className={`transition-transform duration-200 ${
									isExpanded ? 'rotate-180' : ''
								}`}
							/>
						</button>
					)}
				</div>
				{hasChildren && isExpanded && (
					<div className="animate-in slide-in-from-top-2 mt-1 ml-2 duration-200">
						{category.children.map(child => renderCategoryOption(child))}
					</div>
				)}
			</div>
		)
	}

	return (
		<Sheet open={isOpen} onOpenChange={onOpenChange}>
			<SheetTrigger asChild>{trigger}</SheetTrigger>
			<SheetContent
				className="flex h-full w-full flex-col md:w-lg"
				onOpenAutoFocus={e => e.preventDefault()}
			>
				<SheetHeader className="flex-shrink-0">
					<SheetTitle>{t('action_add_categories')}</SheetTitle>
					<SheetDescription>
						{t('misc_select_categories_to_filter')}
					</SheetDescription>
				</SheetHeader>

				<div className="flex-1 overflow-y-auto">
					<div className="flex flex-col gap-4 p-4">
						{/* Search input */}
						<div className="relative">
							<div className="absolute inset-y-0 left-0 flex items-center pl-3">
								<Icon name="search" size="sm" className="text-foreground/50" />
							</div>
							<input
								type="text"
								placeholder={t('form_label_category_search')}
								value={searchQuery}
								onChange={e => setSearchQuery(e.target.value)}
								className="border-border bg-background focus:border-primary focus:ring-primary/20 w-full rounded-lg border py-3 pr-4 pl-10 text-sm transition-all duration-200 focus:ring-2 focus:outline-hidden"
							/>
							{searchQuery && (
								<button
									type="button"
									onClick={() => setSearchQuery('')}
									className="text-foreground/50 hover:text-foreground absolute inset-y-0 right-0 flex items-center pr-3"
								>
									<Icon name="cross" size="xs" />
								</button>
							)}
						</div>

						{/* All categories option */}
						<div className="mb-2">
							<button
								type="button"
								onClick={handleAllToggle}
								className={`group w-full rounded-lg p-4 transition-all duration-200 ${
									isAllSelected
										? 'from-secondary/15 to-primary/10 border-secondary border-l-3 bg-gradient-to-r'
										: 'hover:bg-foreground/5 border-border border hover:shadow-sm'
								}`}
							>
								<div className="flex items-center gap-3">
									<div
										className={`flex h-5 w-5 items-center justify-center rounded-md border-2 transition-all duration-200 ${
											isAllSelected
												? 'border-secondary bg-secondary text-secondary-foreground scale-110'
												: 'border-border bg-background group-hover:border-secondary/50'
										}`}
									>
										{isAllSelected && (
											<Icon
												name="check"
												size="xs"
												className="animate-in fade-in zoom-in duration-200"
											/>
										)}
									</div>
									<div className="from-secondary to-primary flex h-8 w-8 items-center justify-center rounded-full bg-gradient-to-br">
										<Icon name="globe" size="xs" className="text-white" />
									</div>
									<div className="flex flex-col items-start">
										<span
											className={`font-medium transition-colors duration-200 ${
												isAllSelected
													? 'text-secondary'
													: 'text-foreground group-hover:text-secondary'
											}`}
										>
											{t('misc_all_categories')}
										</span>
										<span className="text-foreground/60 text-xs">
											{t('misc_show_events_from_all_categories')}
										</span>
									</div>
								</div>
							</button>
						</div>

						{/* Categories list */}
						<div className="border-t pt-4">
							{filteredCategories.length > 0 ? (
								<div className="space-y-1">
									{filteredCategories.map(category =>
										renderCategoryOption(category),
									)}
								</div>
							) : (
								<div className="flex flex-col items-center justify-center py-8 text-center">
									<Icon
										name="search"
										size="lg"
										className="text-foreground/30 mb-2"
									/>
									<Text className="text-foreground/60 text-sm">
										{t('misc_no_results')}
									</Text>
									<Text className="text-foreground/50 mt-1 text-xs">
										{t('misc_try_different_search_term')}
									</Text>
								</div>
							)}
						</div>

						{/* Selected categories display */}
						{(selectedCategories.length > 0 || isAllSelected) && (
							<div className="border-foreground/20 mt-4 border-t pt-4">
								{/* Header */}
								<div className="mb-3 flex items-center justify-between">
									<Text className="text-foreground text-sm font-medium">
										{isAllSelected
											? t('misc_all_categories')
											: `${selectedCategories.length} ${selectedCategories.length === 1 ? t('misc_category') : t('misc_categories')} ${t('misc_selected')}`}
									</Text>
									{!isAllSelected && selectedCategories.length > 0 && (
										<button
											type="button"
											onClick={handleClearAll}
											className="text-foreground/60 hover:text-destructive text-xs transition-colors duration-200 hover:underline"
										>
											{t('action_clear_all')}
										</button>
									)}
								</div>

								{/* Selected categories chips */}
								<div className="flex flex-wrap gap-2">
									{isAllSelected ? (
										<div className="bg-secondary/20 border-secondary/30 text-secondary-foreground flex items-center rounded-lg border px-3 py-2 text-sm">
											<Icon name="globe" size="xs" className="mr-2" />
											{t('misc_all_categories')}
										</div>
									) : (
										selectedCategories.map(category => (
											<div
												key={category.alias}
												className="bg-card border-foreground/20 group flex items-center rounded-lg border px-3 py-2 text-sm transition-all duration-200 hover:cursor-pointer hover:border-red-400 hover:bg-red-50 hover:shadow-sm dark:hover:bg-red-950/30"
												onClick={() => handleCategoryToggle(category)}
												title={`Remove ${category.name}`}
											>
												<div className="relative mr-2">
													<Icon
														name={getIconName(category.icon)}
														size="xs"
														className="text-foreground/70 transition-opacity duration-200 group-hover:opacity-0"
													/>
													<Icon
														name="cross"
														size="xs"
														className="absolute inset-0 text-red-500 opacity-0 transition-opacity duration-200 group-hover:opacity-100"
													/>
												</div>
												<span className="text-foreground transition-colors duration-200 group-hover:text-red-600 dark:group-hover:text-red-400">
													{category.name}
												</span>
											</div>
										))
									)}
								</div>
							</div>
						)}
					</div>
				</div>

				{/* Action buttons */}
				<div className="flex-shrink-0 border-t p-4">
					<div className="flex justify-between gap-4">
						<Button
							type="button"
							onClick={handleClearAll}
							variant="outline-light"
							size="lg"
							disabled={categories.length === 0}
						>
							{t('action_clear_all')}
						</Button>
						<Button
							type="button"
							onClick={() => onOpenChange(false)}
							variant="secondary"
							size="lg"
						>
							{t('action_choose_categories')}
						</Button>
					</div>
				</div>
			</SheetContent>
		</Sheet>
	)
}
